import { isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { CostNewByCategory } from './costSelectors/selectCostNewByCategory.ts';
import { selectDepreciationSettings } from './orderSelectors.ts';
import {
  selectAllImprovements,
  selectImprovementsById,
} from './propertyDataSelectors/improvementsSelectors.ts';
import { calculateDepreciationRatioForFeature } from './selectDepreciationRatioForFeature.ts';

const calculateDepreciationRatioByCategory = (
  depreciationSettings: ReturnType<typeof selectDepreciationSettings>,
  improvements: ReturnType<typeof selectImprovementsById>,
): { [_key in keyof CostNewByCategory]: number | undefined } => ({
  gla: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  garage: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'garage'),
  carport: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'carport'),
  basementTotal: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  basementFinished: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  attic: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  fireplace: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'fireplace'),
  fullBath: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  halfBath: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  belowGradeFullBath: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  belowGradeHalfBath: calculateDepreciationRatioForFeature(depreciationSettings, improvements),
  shed: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'shed'),
  workshop: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'workshop'),
  barn: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'barn'),
  pool: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'pool'),
  spa: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'spa'),
  adu: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'adu'),
  porch: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'porch'),
  patio: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'patio'),
  deck: calculateDepreciationRatioForFeature(depreciationSettings, improvements, 'deck'),
  manualCost: calculateDepreciationRatioForFeature(
    depreciationSettings,
    improvements,
    'manualCost',
  ),
});

const calculateDepreciationRatioByCategoryForDisplay = (
  depreciationSettings: ReturnType<typeof selectDepreciationSettings>,
  improvements: ReturnType<typeof selectImprovementsById>,
) => {
  const ratios = calculateDepreciationRatioByCategory(depreciationSettings, improvements);

  return Object.fromEntries(
    Object.entries(ratios).map(([key, value]) => [
      key,
      isDefined(value) ? `${(value * 100).toFixed(2)}%` : undefined,
    ]),
  );
};

export const selectDepreciationRatioByCategory = createSelector(
  [selectDepreciationSettings, selectImprovementsById],
  calculateDepreciationRatioByCategory,
);

export const selectDepreciationRatiosByCategory = createSelector(
  [selectDepreciationSettings, selectAllImprovements],
  (depreciationSettings, allImprovements) => {
    if (isUndefined(allImprovements)) {
      return undefined;
    }

    return Object.fromEntries(
      allImprovements.map((improvements) => [
        improvements.id,
        calculateDepreciationRatioByCategory(depreciationSettings, improvements),
      ]),
    );
  },
);

export const selectDepreciationRatiosByCategoryDisplay = createSelector(
  [selectDepreciationSettings, selectAllImprovements],
  (depreciationSettings, allImprovements) => {
    if (isUndefined(allImprovements)) {
      return undefined;
    }

    return Object.fromEntries(
      allImprovements.map((improvements) => [
        improvements.id,
        calculateDepreciationRatioByCategoryForDisplay(depreciationSettings, improvements),
      ]),
    );
  },
);
